import {
  PlayerEvents,
  HeartbeatEvents,
  StreamType,
  PlaybackMode,
  playerStateManager,
  PlayerStatus,
} from '@4tn/core-audio-player-shared';
import type Media from '@api/models/Media';
import EventDispatcher from '@utils/event-dispatcher';
import { INITIAL_CONFIG } from '../consts';
import ConnectionManager from './connection-manager';
import { AUDIO_PLAYER } from './consts';
import { type IAudioPlayer } from './types';
import { createAudioElement } from './utils';
import { type IConfig } from '../types';
import { isOfflineOnFirefox } from '@players/utils/browser';
import { isAbortError } from '@players/utils/error';

class AudioPlayer implements IAudioPlayer {
  private static instance: AudioPlayer;

  static active: boolean;

  protected audioElement: HTMLAudioElement | null = null;

  private config: IConfig = INITIAL_CONFIG;

  private media: Media;

  private uri: string;

  private readonly connectionManager: ConnectionManager;

  // eslint-disable-next-line @typescript-eslint/prefer-readonly
  private currentPlayerStatus = PlayerStatus.IDLE;

  private constructor() {
    this.connectionManager = new ConnectionManager(this);
  }

  public async destroy(): Promise<void> {
    this.removeEventListeners();
    this.audioElement = null;
  }

  static getInstance(): IAudioPlayer {
    if (!AudioPlayer.instance) {
      AudioPlayer.instance = new AudioPlayer();
    }
    AudioPlayer.instance.connectionManager.handleConnections();
    return AudioPlayer.instance;
  }

  public handlePlay(uri: string, config: IConfig, media: Media): Promise<void> {
    this.config = config;
    this.media = media;
    this.uri = uri;

    if (!this.audioElement) {
      return this.handleLoad();
    }
    return this.handleUpdateSrc();
  }

  private handleLoad(): Promise<void> {
    this.audioElement = createAudioElement(this.uri);
    this.addEventListeners();

    if (this.config.currentTime !== undefined) {
      this.audioElement.currentTime = this.config.currentTime;
    }
    this.load();
    return this.play();
  }

  public load(): void {
    if (!this.audioElement) {
      return;
    }
    this.audioElement.load();
  }

  public async play(): Promise<void> {
    if (!this.audioElement) {
      return;
    }
    if (this.config.currentTime) {
      this.audioElement.currentTime = this.config.currentTime;
    }

    const { volumeLevel } = playerStateManager.getState();
    this.audioElement.volume = volumeLevel ?? 1;
    this.audioElement.muted = false;

    const playPromise = this.audioElement.play();
    if (playPromise === undefined) {
      // some older browsers doesn't support play promise, hence we need this fallback
      this.onStreamStart();
      return Promise.resolve();
    }
    return await playPromise.then(this.onStreamStart.bind(this)).catch(async (error) => {
      // we do not want to throw an error in case user interrupted previous stream with another play() call
      if (isAbortError(error)) {
        return;
      }
      return Promise.reject(error);
    });
  }

  public async resume(): Promise<void> {
    if (!this.audioElement) {
      return;
    }
    try {
      await this.audioElement.play();
      EventDispatcher.sendEvent(PlayerEvents.STREAM_RESUMED, {
        name: PlayerEvents.STREAM_RESUMED,
        player: AUDIO_PLAYER,
        contentType: this.media.contentType,
        time: this.audioElement.currentTime,
      });
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async stop(disconnected = false): Promise<void> {
    if (!this.audioElement) {
      return;
    }

    this.audioElement.pause();
    this.audioElement.currentTime = 0;
    this.currentPlayerStatus = disconnected ? PlayerStatus.DISCONNECT : PlayerStatus.PAUSE;

    EventDispatcher.sendEvent(PlayerEvents.STREAM_STOP, {
      name: PlayerEvents.STREAM_STOP,
      player: AUDIO_PLAYER,
      media: this.media,
      time: this.audioElement.currentTime,
    });
  }

  public pause(): void {
    if (!this.audioElement) {
      return;
    }
    this.audioElement.pause();
    this.currentPlayerStatus = PlayerStatus.PAUSE;

    EventDispatcher.sendEvent(PlayerEvents.STREAM_PAUSE, {
      name: PlayerEvents.STREAM_PAUSE,
      player: AUDIO_PLAYER,
      media: this.media,
      time: this.audioElement.currentTime,
    });
  }

  public mute(): void {
    if (!this.audioElement) {
      return;
    }
    this.audioElement.muted = true;
    EventDispatcher.sendEvent(PlayerEvents.STREAM_MUTED, {
      name: PlayerEvents.STREAM_MUTED,
      player: AUDIO_PLAYER,
      time: this.audioElement.currentTime,
    });
  }

  public unmute(): void {
    if (!this.audioElement) {
      return;
    }
    this.audioElement.muted = false;
    EventDispatcher.sendEvent(PlayerEvents.STREAM_UNMUTED, {
      name: PlayerEvents.STREAM_UNMUTED,
      player: AUDIO_PLAYER,
      time: this.audioElement.currentTime,
    });
  }

  public setVolume(level: number): void {
    if (!this.audioElement) {
      return;
    }
    this.audioElement.volume = level;
  }

  public seek(intent: number): void {
    if (!this.audioElement) {
      return;
    }
    this.audioElement.currentTime = intent;
  }

  private handleUpdateSrc(): Promise<void> {
    if (!this.audioElement) {
      return Promise.resolve();
    }
    this.audioElement.src = this.uri;
    // reset currentTime each time media source changes
    this.config.currentTime = undefined;

    return this.play();
  }

  private addEventListeners(): void {
    if (!this.audioElement) {
      return;
    }
    this.audioElement.addEventListener('playing', this.onPlaying);
    this.audioElement.addEventListener('pause', this.onStreamPaused);
    this.audioElement.addEventListener('stalled', this.onStalled);
    this.audioElement.addEventListener('emptied', this.onEmptied);
    this.audioElement.addEventListener('ended', this.onEnded);
    this.audioElement.addEventListener('timeupdate', this.onTimeUpdate);
    this.audioElement.addEventListener('volumechange', this.onVolumeChange);
    this.audioElement.addEventListener('durationchange', this.onDurationChange);
    this.audioElement.addEventListener('loadedmetadata', this.onLoadedMetadata);
    this.audioElement.addEventListener('error', this.onStreamError);
  }

  private removeEventListeners(): void {
    if (!this.audioElement) {
      return;
    }
    this.audioElement.removeEventListener('playing', this.onPlaying);
    this.audioElement.removeEventListener('pause', this.onStreamPaused);
    this.audioElement.removeEventListener('stalled', this.onStalled);
    this.audioElement.removeEventListener('emptied', this.onEmptied);
    this.audioElement.removeEventListener('ended', this.onEnded);
    this.audioElement.removeEventListener('timeupdate', this.onTimeUpdate);
    this.audioElement.removeEventListener('volumechange', this.onVolumeChange);
    this.audioElement.removeEventListener('durationchange', this.onDurationChange);
    this.audioElement.removeEventListener('loadedmetadata', this.onLoadedMetadata);
    this.audioElement.removeEventListener('error', this.onStreamError);
  }

  /*  Event listeners */

  private readonly onPlaying = (): void => {
    if (!this.audioElement) {
      return;
    }
    if (this.currentPlayerStatus !== PlayerStatus.PLAY) {
      EventDispatcher.sendEvent(PlayerEvents.PLAYER_PLAYING, {
        name: PlayerEvents.PLAYER_PLAYING,
        player: AUDIO_PLAYER,
        isLiveAudio: this.media?.contentType !== StreamType.PODCAST,
        uri: this.uri,
        media: this.media,
      });
      EventDispatcher.sendEvent(PlayerEvents.HEARTBEAT, {
        name: HeartbeatEvents.START_HEARTBEAT,
        media: this.media,
      });
    }

    this.currentPlayerStatus = PlayerStatus.PLAY;
  };

  private readonly onStreamPaused = (): void => {
    if (!this.audioElement) {
      return;
    }
    // we want to set the StreamState to 'disconnected' if the stream was paused by the system instead of a user action
    if (this.currentPlayerStatus !== PlayerStatus.PAUSE) {
      this.currentPlayerStatus = PlayerStatus.DISCONNECT;
      EventDispatcher.sendEvent(PlayerEvents.PLAYER_DISCONNECTED);
    }

    EventDispatcher.sendEvent(PlayerEvents.HEARTBEAT, {
      name: HeartbeatEvents.STOP_HEARTBEAT,
      media: this.media,
    });
  };

  private readonly onStreamStart = (): void => {
    if (!this.audioElement) {
      return;
    }
    EventDispatcher.sendEvent(PlayerEvents.STREAM_START, {
      name: PlayerEvents.STREAM_START,
      player: AUDIO_PLAYER,
      isLiveAudio: this.media?.contentType !== StreamType.PODCAST,
      uri: this.uri,
      media: this.media,
    });
  };

  private readonly onEmptied = (): void => {
    if (!this.audioElement) {
      return;
    }
    EventDispatcher.sendEvent(PlayerEvents.PLAYER_EMPTIED, {
      name: PlayerEvents.PLAYER_EMPTIED,
      player: AUDIO_PLAYER,
      media: this.media,
    });
  };

  private readonly onEnded = (): void => {
    if (!this.audioElement) {
      return;
    }
    EventDispatcher.sendEvent(PlayerEvents.HEARTBEAT, {
      name: HeartbeatEvents.RESET_HEARTBEAT,
      media: this.media,
    });
    EventDispatcher.sendEvent(PlayerEvents.STREAM_ENDED, {
      time: this.audioElement.currentTime,
    });
  };

  private readonly onTimeUpdate = (): void => {
    if (!this.audioElement) {
      return;
    }
    EventDispatcher.sendEvent(PlayerEvents.TIME_UPDATE, {
      name: PlayerEvents.TIME_UPDATE,
      time: this.audioElement.currentTime,
    });
    EventDispatcher.sendEvent(PlayerEvents.HEARTBEAT, {
      name: HeartbeatEvents.UPDATE_TIME,
      time: this.audioElement.currentTime,
    });
  };

  private readonly onVolumeChange = (): void => {
    if (!this.audioElement) {
      return;
    }
    EventDispatcher.sendEvent(PlayerEvents.VOLUME_CHANGED, {
      name: PlayerEvents.VOLUME_CHANGED,
      volume: this.audioElement.volume,
    });
  };

  private readonly onDurationChange = (): void => {
    if (!this.audioElement) {
      return;
    }
    EventDispatcher.sendEvent(PlayerEvents.DURATION_CHANGE, {
      name: PlayerEvents.DURATION_CHANGE,
      duration: this.audioElement.duration,
    });
  };

  private readonly onLoadedMetadata = (): void => {
    EventDispatcher.sendEvent(PlayerEvents.STREAM_METADATA_LOADED, {
      name: PlayerEvents.STREAM_METADATA_LOADED,
      player: AUDIO_PLAYER,
      duration: this.audioElement?.duration,
      media: this.media,
    });
  };

  private readonly onStalled = (): void => {
    if (!this.audioElement) {
      return;
    }
    EventDispatcher.sendEvent(PlayerEvents.STREAM_STALLED, {
      name: PlayerEvents.STREAM_STALLED,
      media: this.media,
    });
  };

  private readonly onStreamError = (): void => {
    if (isOfflineOnFirefox() || !this.audioElement || !AudioPlayer.active) {
      return;
    }
    EventDispatcher.sendEvent(PlayerEvents.HEARTBEAT, {
      name: HeartbeatEvents.RESET_HEARTBEAT,
      media: this.media,
    });
    EventDispatcher.sendEvent(PlayerEvents.STREAM_ERROR, {
      name: PlayerEvents.STREAM_ERROR,
      ...this.config,
      streamUrl: this.uri,
      time: this.audioElement.currentTime,
    });
  };

  get duration(): number {
    if (!this.audioElement) {
      return 0;
    }
    return this.audioElement.duration;
  }

  get volumeLevel(): number {
    if (!this.audioElement) {
      return 0;
    }
    return this.audioElement.volume;
  }

  get isPlaying(): boolean {
    if (!this.audioElement) {
      return false;
    }
    return !this.audioElement.paused;
  }

  get currentTime(): number {
    if (!this.audioElement) {
      return 0;
    }
    return this.audioElement.currentTime;
  }

  get isMuted(): boolean {
    if (!this.audioElement) {
      return false;
    }
    return this.audioElement.muted;
  }

  get isLiveAudio(): boolean {
    if (!this.audioElement) {
      return false;
    }
    return this.media?.contentType !== StreamType.PODCAST;
  }

  get playbackMode(): PlaybackMode {
    return this.media?.contentType === StreamType.PODCAST || this.media?.contentType === StreamType.CLIP
      ? PlaybackMode.ON_DEMAND_CLIP
      : PlaybackMode.LIVE_AUDIO;
  }
}

export default AudioPlayer;
